import { userIsReadOnly } from "../../../auth/AuthService";
import { ApplicationUser } from "../../users/User.types";
import { calculatedRenewalTooltip } from "../Case.functions";
import { Case, CaseType, RenewalStatus } from "../Case.types";

export enum Editability {
    Editable,
    DisabledPermission,
    DisabledMainCase,
    DisabledDateFuture,
    DisabledDatePast,
    DisabledWatches,
    ReadOnlyUser = 6,
    DisabledCalculatedRenewal = 7,
}

//editSetRenewalStatus is true for users with employee or admin privileges
export function getRenewalEditability(selectedCase: Case, editSetRenewalStatus: boolean, verifiedUser: ApplicationUser | null): Editability {
    if (selectedCase.isCalculatedRenewal) return Editability.DisabledCalculatedRenewal;
    if (userIsReadOnly(verifiedUser)) return Editability.ReadOnlyUser;
    if (selectedCase.isMainCase) {
        //if the renewaldate on the designation case differs from the maincase, this means we can't lock based on the status of the main case
        //this is because some cases in the US can have a different renewaldate than the IP maincase, and might not be renewable because of this
        if (!selectedCase.renewalDateDiffersFromMainCase) {
            if (selectedCase.mainCaseRenewalStatus === RenewalStatus.Expire || selectedCase.mainCaseRenewalStatus === RenewalStatus.Awaiting) {
                return Editability.DisabledMainCase;
            }
        }
    }

    const canEditBasedOnStatus = editSetRenewalStatus || selectedCase.renewalStatus === RenewalStatus.Awaiting || selectedCase.renewalStatus === null;

    if (!canEditBasedOnStatus) return Editability.DisabledPermission;

    /*
        cases that have already expired cannot be updated
        cases that expire in the current year can be updated
        after the first of august, cases that expire in the following year can be updated
    */
    const now = new Date();
    const cutOff = new Date(now.getFullYear(), 7, 1);
    const renewalDate = new Date(selectedCase.renewalDate);
    let canEditBasedOnDate = true;

    if (renewalDate < now) return Editability.DisabledDatePast;

    // Watch cases cannot be updated if the current date is 1 months or less before the renewal date
    //the editSetRenewalStatus is true, we don't check for this date for watches - this will be true for employees and admins
    if (selectedCase.type === CaseType.Watches && !editSetRenewalStatus) {
        const inTwoMonths = new Date();
        inTwoMonths.setMonth(inTwoMonths.getMonth() + 1);
        if (renewalDate <= inTwoMonths) return Editability.DisabledWatches;
    }

    if (now.getFullYear() === renewalDate.getFullYear()) {
        canEditBasedOnDate = true;
    } else {
        canEditBasedOnDate = now >= cutOff && renewalDate.getFullYear() === now.getFullYear() + 1;
    }

    //for watches, they can always be renewed/expired up to one year in advance
    if (selectedCase.type === CaseType.Watches) {
        const watchCutOff = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
        if (renewalDate <= watchCutOff) canEditBasedOnDate = true;
    }

    if (!canEditBasedOnDate) return Editability.DisabledDateFuture;

    return Editability.Editable;
}

export function getEditabilityTooltip(editability: Editability, selectedCase: Case): string {
    if (editability === Editability.DisabledCalculatedRenewal) return calculatedRenewalTooltip;
    if (editability === Editability.DisabledPermission)
        return "If you wish to change the renewal status please write this in the renewal remark, and your caseworker will help proceed the action.";
    if (editability === Editability.DisabledMainCase)
        return selectedCase.mainCaseRenewalStatus === RenewalStatus.Awaiting
            ? `This is a ${selectedCase.mainCaseCountry} designation. Please choose the renewal status of the basic registration first`
            : `This is a ${selectedCase.mainCaseCountry} designation. Renewal status cannot be changed, while the basic registration's status is expired.`;
    if (editability === Editability.DisabledDateFuture) return "You cannot set a renewal status for this registration yet.";
    if (editability === Editability.DisabledDatePast) return "You cannot set a renewal status for this registration because the renewal date has expired.";
    if (editability === Editability.DisabledWatches)
        return "You cannot set a renewal status for this registration because there is less than two months left until the renewal date.";
    return "";
}
